import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { DEFAULT_PERIOD, type DailyQcEntry, type DailyQcField, type DailyQcStatus } from './dailyQcApi';

/** Display helpers shared by the Daily QC screens (presentation only — every rule is the API's). */

/** The history view's own settings, kept by the parent so they survive a trip to the entry form. */
export interface DailyQcHistoryState { from: string; to: string; period: string; search: string; trendField: string | null }

/** Status pill — the shared pill colours: Pass green · Fail red · Incomplete amber. */
export const STATUS_PILL: Record<DailyQcStatus, string> = { Pass: 'ok', Fail: 'alert', Incomplete: 'warn' };

/** Numbers as the prototype shows them (at most 2 decimals, no trailing zeros). */
export function fmtNum(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '';
  return String(Number(v.toFixed(2)));
}

/** A stored value as the history grid shows it. */
export function showValue(f: DailyQcField, v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  switch (f.type) {
    case 'Checkbox': return v === '1' ? '✓' : v === '0' ? '✗' : '';
    case 'Date': return fmtLocalDate(v) || v;
    case 'DateTime': return fmtLocalDateTime(v) || v;
    case 'Numeric': { const n = Number.parseFloat(v); return Number.isFinite(n) ? fmtNum(n) : v; }
    default: return v;
  }
}

/** Initial history state for a logbook opened from the list — G5's 1M; the API fills From/To. */
export function initialHistoryState(): DailyQcHistoryState {
  return { period: DEFAULT_PERIOD, from: '', to: '', search: '', trendField: null };
}

/** One plotted value: its entry date, the value, and whether the field passed. */
export interface TrendPoint { d: string; v: number; ok: boolean | null; current?: boolean }

/** The field's numeric series across the given entries, oldest → newest. The value is the
 *  stored one (calculated fields are stored too), pass/fail is the server's. */
export function seriesFor(field: DailyQcField, entries: DailyQcEntry[]): TrendPoint[] {
  return entries
    .map((e) => ({ d: e.values['Date'] ?? '', v: Number.parseFloat(e.values[field.name] ?? ''), ok: e.accept[field.name] ?? null }))
    .filter((p) => Number.isFinite(p.v))
    .sort((a, b) => a.d.localeCompare(b.d));
}

/** n / mean / sample SD / out-of-limit count of a series. */
export function seriesStats(pts: TrendPoint[]): { n: number; mean: number; sd: number; out: number } | null {
  if (pts.length === 0) return null;
  const vals = pts.map((p) => p.v);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const sd = vals.length > 1 ? Math.sqrt(vals.reduce((a, b) => a + (b - mean) ** 2, 0) / (vals.length - 1)) : 0;
  return { n: vals.length, mean, sd, out: pts.filter((p) => p.ok === false).length };
}

export const opText = (op: string) => op.replace('>=', '≥').replace('<=', '≤');
